import type { MetricExportEntry } from '@/modules/analytics/types';

// Pure shaping of registered report metrics into export payloads. Kept free of
// React/catalog dependencies so the JSON/CSV structure is unit-testable in
// isolation — the catalog lookups happen upstream (group labels are resolved by
// the MetricCard before registration).

type MetricJson = {
  name: string;
  metric_id?: string;
  range: MetricExportEntry['range'];
  timeseries: { series: string; date: string; value: number; value_formatted: string }[];
  breakdown?: Record<string, string | number>[];
};

const buildTimeseries = (entry: MetricExportEntry) =>
  entry.series.flatMap((s) =>
    s.data.map((point) => ({
      series: s.label,
      date: point.date,
      value: point.value,
      // The on-screen string ("88%", "1m 30s", "4.3/5") so a consumer can tell
      // counts from percentages from durations — the bare value has no unit.
      value_formatted: entry.valueFormatter(point.value),
    }))
  );

// One breakdown row's dimension cells, keyed by human label (e.g. "AI Agent" →
// "Sales Bot"). Shared by the JSON and CSV paths so they stay in lockstep.
const buildGroupCells = (
  entry: MetricExportEntry,
  row: MetricExportEntry['breakdown'][number]
): Record<string, string> => {
  const cells: Record<string, string> = {};
  for (const column of entry.groupColumns) {
    cells[entry.groupColumnLabels[column] ?? column] = row.groupValues[column]?.label ?? '';
  }
  return cells;
};

const buildBreakdown = (entry: MetricExportEntry): Record<string, string | number>[] =>
  entry.breakdown.map((row) => ({
    ...buildGroupCells(entry, row),
    value: row.value,
    value_formatted: entry.valueFormatter(row.value),
  }));

// One metric's JSON shape — used both as an element of a whole-report export and
// as the standalone payload for a single-metric download.
export const buildMetricJson = (entry: MetricExportEntry): MetricJson => {
  const breakdown = buildBreakdown(entry);
  return {
    name: entry.name,
    ...(entry.metricId ? { metric_id: entry.metricId } : {}),
    range: entry.range,
    timeseries: buildTimeseries(entry),
    ...(breakdown.length > 0 ? { breakdown } : {}),
  };
};

export const buildReportJsonPayload = (reportName: string, entries: MetricExportEntry[]) => ({
  report: reportName,
  metrics: entries.filter((entry) => entry.hasData).map(buildMetricJson),
});

// Value columns always trail the sheet, in this order, so it reads
// metric → discriminators → dimensions → value → value_formatted.
const TRAILING_FIELDS = ['value', 'value_formatted'] as const;

// Normalize heterogeneous rows to a single shared column set. Papa.unparse
// derives its fields from the FIRST row only and drops keys that appear later,
// so a metric grouped by a dimension the first row lacks would silently lose
// that column. Union every key (first-seen order), then append the trailing
// value columns last.
const normalizeRows = (rows: Record<string, unknown>[]): Record<string, unknown>[] => {
  if (rows.length === 0) {
    return [];
  }
  const fields: string[] = [];
  const seen = new Set<string>(TRAILING_FIELDS);
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!seen.has(key)) {
        seen.add(key);
        fields.push(key);
      }
    }
  }
  // Callers always emit both value columns, so append them unconditionally.
  const allFields = [...fields, ...TRAILING_FIELDS];
  return rows.map((row) => {
    const normalized: Record<string, unknown> = {};
    for (const field of allFields) {
      normalized[field] = row[field] ?? '';
    }
    return normalized;
  });
};

// One flat sheet across every metric. Rows are discriminated by `metric` (the
// card title), `metric_index` (stable per card, so duplicate names stay
// separable) and `row_type` ('timeseries' | 'breakdown'); group dimensions
// become their own columns. Metrics with different dimensions coexist via the
// normalized column union above.
export const flattenReportForCsv = (entries: MetricExportEntry[]): Record<string, unknown>[] => {
  const rows: Record<string, unknown>[] = [];
  for (const entry of entries) {
    if (!entry.hasData) {
      continue;
    }
    for (const series of entry.series) {
      for (const point of series.data) {
        rows.push({
          metric: entry.name,
          metric_index: entry.index,
          row_type: 'timeseries',
          series: series.label,
          date: point.date,
          value: point.value,
          value_formatted: entry.valueFormatter(point.value),
        });
      }
    }
    for (const row of entry.breakdown) {
      rows.push({
        metric: entry.name,
        metric_index: entry.index,
        row_type: 'breakdown',
        ...buildGroupCells(entry, row),
        value: row.value,
        value_formatted: entry.valueFormatter(row.value),
      });
    }
  }
  return normalizeRows(rows);
};
